import { capitalize } from '@mui/material'
import { RpcOptions, ServiceInfo, UnaryCall } from '@protobuf-ts/runtime-rpc'
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { useOAuth } from 'lib/rpc'
import { useClientRPC } from 'lib/rpcts'
import { WebsocketNotifyClient } from 'lib/websocket-notify'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { action } from './actions'
import { Flags } from './GRPCSubscriptionObject'
import { grpcReducerNamespace, ReduxObjectCache, ReduxObjectState, SerializedErrorType } from './reducer'
import { AnyMessage, GRPCClient, InferRequest, serializeQuery, UnaryMethod, InferUnaryResponse, clientTypeName } from './utils'

export type HookName<S extends string> = `use${Capitalize<S>}`
export type UseHook<Client, Method extends keyof Client & string, ResponseType = InferUnaryResponse<Client[Method]>> = (
    args: InferRequest<Client[Method]>,
    flags?: HookFlags
) => ReduxObjectCache<ResponseType>

type getDataFnType = (force?: boolean) => void
export type TriggerOpts = { force: boolean } & HookFlags
export type LazyHookName<S extends string> = `useLazy${Capitalize<S>}`
export type UseLazyHook<
    Client,
    Method extends keyof Client & string,
    ResponseType = InferUnaryResponse<Client[Method]>
> = () => {
    trigger: (args: InferRequest<Client[Method]>, opts?: TriggerOpts) => {
        getData?: getDataFnType,
        request?: Promise<unknown>
    }
} & ReduxObjectCache<ResponseType>

export type InvalidateHookName<S extends string> = `useInvalidate${Capitalize<S>}`
export type InvalidateHook<Client, Method extends keyof Client & string> = (
    args?: InferRequest<Client[Method]>
) => () => void

export type InvalidateHookLike = () => () => void

export type UnaryHookLike<Req, ResponseType> = (
    args: Req,
    flags?: HookFlags
) => ReduxObjectCache<ResponseType>

type ObjectHooks<Client extends ServiceInfo, Method extends keyof Client & string, ResponseType> = Record<
    HookName<Method>,
    UseHook<Client, Method, ResponseType>
> &
    Record<LazyHookName<Method>, UseLazyHook<Client, Method, ResponseType>> &
    Record<InvalidateHookName<Method>, InvalidateHook<Client, Method>>

type CreateApiForObjectReturn<
    Client extends ServiceInfo,
    Method extends keyof Client & string,
    Res extends object
> = ObjectHooks<Client, Method, Res> & { reducer: string }

export type HookFlags = Omit<Flags<unknown>, 'transformResponse' | 'deprecatedNotify' | 'notify'> & {
    actionable?: boolean
    notify?: boolean
}

export type InvokeObjectRPC<Req extends object, Res extends object> = (opts: RpcOptions) => UnaryCall<Req, Res>
export type InvokeListRPC<Req extends object, Res extends object> = (
    pageToken: string,
    pageSize: number,
    opts: RpcOptions
) => UnaryCall<Req, Res>

const DEFAULT_STATE = Object.freeze({
    isLoading: true,
    isBackgroundLoading: false,
    isError: false,
    data: undefined,
    error: undefined,
})

const LAZY_DEFAULT_STATE = Object.freeze({
    isLoading: false,
    isBackgroundLoading: false,
    isError: false,
    data: undefined,
    error: undefined,
})

export type UnaryHookResponse<ResponseType> = {
    readonly isLoading: boolean
    readonly isBackgroundLoading: boolean
    readonly isError: boolean
    readonly data?: Readonly<ResponseType>
    readonly error?: Readonly<SerializedErrorType>
}

export type UnaryHookNoCache<Client, Method extends keyof Client & string, ResponseType = InferUnaryResponse<Client[Method]>> = (
    args: InferRequest<Client[Method]>
) => UnaryHookResponse<ResponseType>

export type LazyHookNoCache<Client, Method extends keyof Client & string, ResponseType = InferUnaryResponse<Client[Method]>> = () => {
    trigger: (args: InferRequest<Client[Method]>) => Promise<ResponseType>
} & UnaryHookResponse<ResponseType>

export type CreateApiForObjectNoCacheReturn<
    Client extends ServiceInfo,
    Method extends keyof Client & string,
    Res extends object
> = {
    [K in `use${Capitalize<Method>}`]: UnaryHookNoCache<Client, Method, Res>
} & {
        [K in `useLazy${Capitalize<Method>}`]: LazyHookNoCache<Client, Method, Res>
    }

/**
 * Creates a set of hooks and a reducer entry to manage unary data (ie, a single object)
 * for a specific gRPC service method, ie (GetApp, GetUser). The generated hooks
 * provide subscription, invalidation, and lazy-loading capabilities.
 *
 * @param {GRPCClient<Client>} ClientConstructor The protobuf-generated constructor for your generated gRPC client.
 * @param {Method} methodName The gRPC method name that returns a unary response.
 * @param {(i: Res) => TransformedRes} [transformResponse] An optional transformation function
 *        that can be applied to the raw response, to manipulate the data before it's stored in the redux store.
 * @param {string} [tag] The typeName of a ref object, used to match websocket updates to this object.
 * @returns {CreateApiForObjectReturn<Client, Method, TransformedRes>} An object containing:
 *   - reducer: the namespace used in the Redux store,
 *   - use{methodName} hooks that interact with the Redux store and the underlying gRPC method,
 *   - a lazy variant of the hook
 *   - a invalidate hook, to invalidate the data
 */

export const createUnaryApi = <
    Client extends ServiceInfo,
    Method extends keyof Client & string,
    Req extends AnyMessage,
    Res extends object = InferUnaryResponse<Client[Method]>,
    TransformedRes extends object = Res // TODO, pass in getState...
>(ClientConstructor: GRPCClient<Client>, methodName: Method, transformResponse?: (i: Res) => TransformedRes, tag?: string):
    CreateApiForObjectReturn<Client, Method, TransformedRes> => {
    type Request = InferRequest<Client[Method]>
    type Selector = (state: ReduxObjectState<TransformedRes>) => ReduxObjectCache<TransformedRes>

    const namespace = clientTypeName(ClientConstructor)

    const subscribe = (
        key: string,
        client: Client,
        request: Request,
        flags: Flags<TransformedRes> = {},
    ): ((
        dispatch: ThunkDispatch<ReduxObjectState<object>, never, UnknownAction>,
        getState: () => ReduxObjectState<object>,
    ) => Promise<unknown>) =>
        action.subscribe<Req, Res>(
            namespace,
            key,
            (opts) => {
                const m = client[methodName] as UnaryMethod<Req>
                return m.call(client, request, opts)
            },
            { ...flags, tag, transformResponse },
        )

    const useHook: UseHook<Client, Method, TransformedRes> = (args: Request, flags?: HookFlags) => {
        // TODO(kans): warn dev if flags aren't stable, because we won't update flags on active subs atm
        const client = useClientRPC(ClientConstructor)

        const authClient = useOAuth()
        const notify = useMemo(() => {
            return flags?.notify
                ? WebsocketNotifyClient.getInstance(authClient)
                : undefined
        }, [authClient, flags?.notify])

        const { key, selector } = useMemo(() => {
            const k = serializeQuery(methodName, args)
            const s: Selector = (state) => state[grpcReducerNamespace]?.[namespace]?.[key] ?? DEFAULT_STATE
            return { key: k, selector: s }
        }, [args])

        const dispatch: ThunkDispatch<ReduxObjectState<TransformedRes>, never, UnknownAction> = useDispatch()

        useEffect(() => {
            if (!client || flags?.actionable === false) {
                return
            }
            dispatch(subscribe(key, client, args, { ...flags, notify }))
            return () => {
                void dispatch(action.unsubscribe(namespace, key))
            }
        }, [key, client, flags?.actionable]) // eslint-disable-line react-hooks/exhaustive-deps

        return useSelector(selector)
    }

    const useLazyHook: UseLazyHook<Client, Method, TransformedRes> = () => {
        const client = useClientRPC(ClientConstructor)
        type lazyRefType = { key: string; args: Request; unsub: () => void; getData: getDataFnType }
        const ref = useRef<lazyRefType | undefined>(undefined)
        const [selector, setSelector] = useState<Selector>(() => () => LAZY_DEFAULT_STATE)
        const dispatch: ThunkDispatch<ReduxObjectState<TransformedRes>, never, UnknownAction> = useDispatch()

        const authClient = useOAuth()

        const trigger = useCallback(
            (args: Request, flags?: TriggerOpts) => {
                if (!client) {
                    return {}
                }

                const key = serializeQuery(methodName, args)

                const notify = flags?.notify
                    ? WebsocketNotifyClient.getInstance(authClient)
                    : undefined

                // This can refetch stale data...
                //   we could look to see if its stale before doing this!
                ref.current?.unsub?.()
                if (flags?.actionable === false) {
                    return {}
                }
                const request = dispatch(subscribe(key, client, args, { ...flags, notify }))

                const getData = (force = false): Promise<boolean> => {
                    return dispatch(action.getData(namespace, key, force))
                }

                if (ref.current?.key === key) {
                    return { getData: ref.current?.getData, request }
                }

                ref.current = {
                    key,
                    args,
                    getData,
                    unsub: () => void dispatch(action.unsubscribe(namespace, key)),
                }

                const newSelector = (state: ReduxObjectState<TransformedRes>): ReduxObjectCache<TransformedRes> => {
                    if (!key) {
                        return LAZY_DEFAULT_STATE
                    }
                    return state[grpcReducerNamespace]?.[namespace]?.[key] || LAZY_DEFAULT_STATE
                }
                setSelector(() => newSelector)

                return { getData: ref.current.getData, request }
            },
            [client, authClient, dispatch],
        )

        // State doesn't seem to live this long, so we use a ref :-/
        useEffect(() => () => ref.current?.unsub?.(), [])

        // useSelector swaps out the subscription every time the selector changes
        const selection = useSelector(selector)
        return useMemo(() => ({ ...selection, trigger }), [selection, trigger])
    }

    /*
        Invalidates everything without args
    */
    const useInvalidate: InvalidateHook<Client, Method> = (args?: Request) => {
        const dispatch: ThunkDispatch<ReduxObjectState<TransformedRes>, never, UnknownAction> = useDispatch()
        const key = serializeQuery(methodName, args || null)
        const invalidateAll = !args
        return useCallback(
            () => dispatch(action.invalidate(namespace, key, invalidateAll)),
            [dispatch, key, invalidateAll],
        )
    }
    const capitalized = capitalize(methodName)
    return {
        reducer: namespace,
        [`use${capitalized}`]: useHook,
        [`useLazy${capitalized}`]: useLazyHook,
        [`useInvalidate${capitalized}`]: useInvalidate,
    } as CreateApiForObjectReturn<Client, Method, TransformedRes>
}

export const createUnaryApiNoCache = <
    Client extends ServiceInfo,
    Method extends keyof Client & string,
    Req extends AnyMessage,
    Res extends object = InferUnaryResponse<Client[Method]>,
    TransformedRes extends object = Res
>(ClientConstructor: GRPCClient<Client>, methodName: Method, transformResponse?: (i: Res) => TransformedRes):
    CreateApiForObjectNoCacheReturn<Client, Method, TransformedRes> => {
    type Request = InferRequest<Client[Method]>

    const useHook: UnaryHookNoCache<Client, Method, TransformedRes> = (args: Request) => {
        const client = useClientRPC(ClientConstructor)
        const [state, setState] = useState<UnaryHookResponse<TransformedRes>>({
            isLoading: true,
            isBackgroundLoading: false,
            isError: false,
            data: undefined,
            error: undefined,
        })

        // Memoize the args to prevent unnecessary re-renders
        const argsValue = JSON.stringify(args)
        const memoizedArgs = useMemo(() => argsValue, [argsValue])

        useEffect(() => {
            if (!client) {
                return
            }

            let isMounted = true
            const controller = new AbortController()

            const fetchData = async (): Promise<void> => {
                try {
                    const m = client[methodName] as UnaryMethod<Req>
                    const response = await m.call(client, JSON.parse(memoizedArgs), { signal: controller.signal })
                    const transformed = transformResponse ? transformResponse(response.response) : response.response

                    if (isMounted) {
                        setState({
                            isLoading: false,
                            isBackgroundLoading: false,
                            isError: false,
                            data: transformed,
                            error: undefined,
                        })
                    }
                } catch (error) {
                    if (isMounted) {
                        setState({
                            isLoading: false,
                            isBackgroundLoading: false,
                            isError: true,
                            data: undefined,
                            error: error as SerializedErrorType,
                        })
                    }
                }
            }

            void fetchData()

            return () => {
                isMounted = false
                controller.abort()
            }
        }, [client, memoizedArgs]) // Only depend on memoized args

        return state
    }

    const useLazyHook: LazyHookNoCache<Client, Method, TransformedRes> = () => {
        const client = useClientRPC(ClientConstructor)
        const [state, setState] = useState<UnaryHookResponse<TransformedRes>>({
            isLoading: false,
            isBackgroundLoading: false,
            isError: false,
            data: undefined,
            error: undefined,
        })

        const trigger = useCallback(
            async (args: Request): Promise<TransformedRes> => {
                if (!client) {
                    throw new Error('Client not initialized')
                }

                setState({
                    isLoading: true,
                    isBackgroundLoading: false,
                    isError: false,
                    data: undefined,
                    error: undefined,
                })

                try {
                    const m = client[methodName] as UnaryMethod<Req>
                    const response = await m.call(client, args)
                    const transformed = transformResponse ? transformResponse(response.response) : response.response

                    setState({
                        isLoading: false,
                        isBackgroundLoading: false,
                        isError: false,
                        data: transformed,
                        error: undefined,
                    })

                    return transformed
                } catch (error) {
                    setState({
                        isLoading: false,
                        isBackgroundLoading: false,
                        isError: true,
                        data: undefined,
                        error: error as SerializedErrorType,
                    })
                    throw error
                }
            },
            [client],
        )

        return { ...state, trigger }
    }

    const capitalized = capitalize(methodName)
    return {
        [`use${capitalized}`]: useHook,
        [`useLazy${capitalized}`]: useLazyHook,
    } as CreateApiForObjectNoCacheReturn<Client, Method, TransformedRes>
}
